import { useNavigate } from 'react-router-dom';
import { useRecording, formatRecordingTime } from '@/contexts/RecordingContext';
import { Button } from '@/components/ui/button';
import { Square, Play, Pause, Keyboard } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function RecordingBar() {
  const navigate = useNavigate();
  const {
    isRecording,
    isPaused,
    recordingTime,
    stopRecording,
    pauseRecording,
    resumeRecording,
    session,
  } = useRecording();

  if (!isRecording) return null;

  const handleStop = async () => {
    await stopRecording();
    navigate('/consultas');
  };

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-destructive text-destructive-foreground shadow-lg animate-fade-in">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Left: Recording indicator */}
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
            </span>
            <span className="font-medium">
              {isPaused ? 'Pausado' : 'Gravando'} • {formatRecordingTime(recordingTime)}
            </span>
            {session.patientName && (
              <span className="text-destructive-foreground/80 hidden sm:inline">
                — {session.patientName}
              </span>
            )}
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-2">
            {/* Pause/Resume button */}
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive-foreground hover:bg-white/20 hover:text-destructive-foreground"
              onClick={isPaused ? resumeRecording : pauseRecording}
            >
              {isPaused ? (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Retomar</span>
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Pausar</span>
                </>
              )}
            </Button>

            {/* Stop button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-white text-destructive hover:bg-white/90 font-semibold"
                  onClick={handleStop}
                >
                  <Square className="w-4 h-4 mr-1 fill-current" />
                  ENCERRAR
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="flex items-center gap-1">
                <Keyboard className="w-3 h-3" />
                <span>{shortcutKey}+Shift+.</span>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
